import _ from "lodash";
import PropTypes from "prop-types";
import { Button, createTheme, ThemeProvider } from "@mui/material";
import baseTheme from "../themes/baseTheme";

const grey = baseTheme.palette.grey;
const blue = baseTheme.palette.blue;

const defaultStyles = {
  padding: "0.25rem 0.6rem",
  fontSize: "0.9rem",
  height: "2.2rem",
  lineHeight: "1.25",
  minWidth: "1.2rem",
  border: "solid 1px",
  fontFamily: "Inter Medium",
  textTransform: "none",
  "& svg + span": {
    marginLeft: "0.4rem",
  },
  "& span + svg": {
    marginLeft: "0.4rem",
  },
  "&.icon-only": {
    padding: "0.25rem 0.6rem",
  },
};

const sizes = {
  default: defaultStyles,
  small: _.merge(_.cloneDeep(defaultStyles), {
    padding: "0.15rem 0.5rem",
    height: "2rem",
    "&.icon-only": {
      padding: "0.5rem",
    },
  }),
  large: _.merge(_.cloneDeep(defaultStyles), {
    padding: "0.5rem 0.8rem",
    height: "2.5rem",
    fontSize: "1.2rem",
    lineHeight: "1.5",
    border: "solid 1px",
    "&.icon-only": {
      padding: "0.8rem",
    },
  }),
};

const roles = {
  default: {
    color: grey[0],
    backgroundColor: blue[50],
    borderColor: blue[50],
    ":hover": {
      backgroundColor: blue[60],
      borderColor: blue[60],
    },
    "&:disabled": {
      color: grey[0],
      backgroundColor: blue[40],
      borderColor: blue[40],
    },
    "& svg": {
      stroke: grey[0],
    },
  },
  secondary: {
    color: blue[50],
    backgroundColor: blue[30],
    borderColor: blue[30],
    ":hover": {
      backgroundColor: blue[40],
      borderColor: blue[40],
    },
    "&:disabled": {
      color: blue[40],
      backgroundColor: blue[15],
      borderColor: blue[15],
      "& svg": {
        stroke: blue[40],
      },
    },
    "& svg": {
      stroke: blue[50],
    },
  },
  tertiary: {
    color: grey[100],
    backgroundColor: grey[0],
    borderColor: grey[30],
    ":hover": {
      backgroundColor: grey[20],
      borderColor: grey[30],
    },
    "&:disabled": {
      color: grey[50],
      backgroundColor: grey[10],
      borderColor: grey[10],
    },
    "& svg": {
      stroke: grey[80],
    },
  },
  linkGrey: {
    height: "2rem",
    padding: "0px",
    color: grey[80],
    backgroundColor: "transparent",
    borderColor: "transparent",
    ":hover": {
      color: grey[90],
      backgroundColor: "transparent",
      borderColor: "transparent",
      "& svg": {
        stroke: grey[90],
        strokeWidth: 2,
        "& path": {
          stroke: grey[90],
          strokeWidth: 2,
        },
      },
    },
    "&:disabled": {
      color: grey[50],
      backgroundColor: "transparent",
      borderColor: "transparent",
      "& svg": {
        stroke: grey[50],
        "& path": {
          stroke: grey[50],
        },
      },
    },
    "& svg": {
      stroke: grey[80],
      "& path": {
        stroke: grey[80],
      },
    },
  },
  linkBlue: {
    height: "2rem",
    padding: "0px",
    color: blue[50],
    backgroundColor: "transparent",
    borderColor: "transparent",
    ":hover": {
      color: blue[60],
      backgroundColor: "transparent",
      borderColor: "transparent",
      "& svg": {
        stroke: blue[60],
      },
    },
    "&:disabled": {
      color: grey[50],
      backgroundColor: "transparent",
      borderColor: "transparent",
      "& svg": {
        stroke: grey[50],
      },
    },
    "& svg": {
      stroke: blue[50],
    },
  },
  danger: {
    color: "#ffffff",
    backgroundColor: "#ff0000",
    borderColor: "#ff0000",
    ":hover": {
      backgroundColor: "#ff3333",
      borderColor: "#ff3333",
    },
    "&:disabled": {
      color: "#ffffff",
      backgroundColor: "#ff9999",
      borderColor: "#ff9999",
    },
    "& svg": {
      stroke: "#ffffff",
    },
  },
  linkDanger: {
    color: "#ff0000",
    backgroundColor: "transparent",
    borderColor: "transparent",
    ":hover": {
      color: "#ff3333",
      backgroundColor: "transparent",
      borderColor: "transparent",
      "& svg": {
        stroke: "#ff3333",
      },
    },
    "&:disabled": {
      color: "#ff9999",
      backgroundColor: "transparent",
      borderColor: "transparent",
      "& svg": {
        stroke: "#ff9999",
      },
    },
    "& svg": {
      stroke: "#ff0000",
    },
  },
};

function getTheme(size, role) {
  const sizeTheme = _.cloneDeep(sizes[size] || sizes["default"]);
  const roleTheme = _.cloneDeep(roles[role] || roles["default"]);
  return createTheme({
    components: {
      MuiButton: {
        styleOverrides: {
          root: _.merge(sizeTheme, roleTheme),
        },
      },
    },
  });
}

export default function ({
  label,
  size,
  role,
  variant,
  leadingComponent,
  trailingComponent,
  disabled = false,
  className,
  disabledMessage = "",
  ...props
}) {
  let labelClassName = "";
  if (leadingComponent) {
    labelClassName = `${labelClassName} has-leading`;
  }
  if (trailingComponent) {
    labelClassName = `${labelClassName} has-trailing`;
  }
  let buttonClassName = className;
  if (!label) {
    buttonClassName = `${buttonClassName} icon-only`;
  }
  return (
    <ThemeProvider theme={getTheme(size, role)}>
      <Button disabled={disabled} {...props} className={buttonClassName}>
        {leadingComponent}
        {label ? <span className={labelClassName}>{label}</span> : undefined}
        {trailingComponent}
      </Button>
    </ThemeProvider>
  );
}

Button.propTypes = {
  /**
   * Button label
   */
  label: PropTypes.string,
  /**
   * Button size
   */
  size: PropTypes.string,
  /**
   * Icon: Component name
   */
  icon: PropTypes.func,
  /**
   * Optional click handler
   */
  onClick: PropTypes.func,
};

Button.defaultProps = {
  size: "default",
  role: "default",
  disabled: false,
  // onClick: undefined,
};
